import { defineStore } from 'pinia'
import axios from '@/api/axios'
import router from '@/router'
import { useAppStore } from './app'
import { useCategoriesStore } from './categories'
import { isPartnerAdmin } from '@/utils/adminPermissions'
import { hasPartnerFeature } from '@/utils/partnerFeatures'
import {
  clearPartnerAuthCookie,
  syncPartnerAuthCookie,
} from '@/utils/partnerAuthCookie'
import { visibleGameCredits } from '@/utils/visibleGameCredits'

function extractTokenFromURL() {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('token')
}

function storeTokenInSessionStorage(token: string) {
  window.sessionStorage.setItem('user', token)
}

function removeTokenFromURL() {
  const url = new URL(window.location.href)
  url.searchParams.delete('token')
  history.replaceState(null, '', url.toString())
}

function isFullAdmin(state: State) {
  return state.role !== null && state.role < 2
}

type State = {
  name: User['fullname'] | null
  role: User['role'] | null
  uid: User['_id'] | null
  userDetails: User | null
  isLoggedIn: boolean
  checked: boolean
}

let lastRedirect = ''

function isHostRedirect(value: unknown): value is string {
  return typeof value === 'string' && /^https?:\/\//i.test(value)
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    name: null,
    role: null,
    uid: null,
    userDetails: null,
    isLoggedIn: false,
    checked: false,
  }),

  getters: {
    credits: state => visibleGameCredits(state.userDetails),
    aiCredits: state => {
      const { userDetails } = state
      if (!userDetails) return 0
      return userDetails.institution?.aiCredits ?? userDetails.aiCredits ?? 0
    },
    isAIEnabled: () => {
      return import.meta.env.VITE_HIDE_AI !== 'true'
    },
    disableRemotesGames: state =>
      state.userDetails?.institution
        ? Boolean(state.userDetails.institution.disableRemotesGames)
        : Boolean(state.userDetails?.disableRemotesGames),
    getUserDetails: state => state.userDetails,
    user: state => state.userDetails,
    institutionDetails: state => state.userDetails?.institution,
    getInstitutionName: state => state.userDetails?.institution?.name,
    getUserName: state => state.name,
    isAuth: state => state.isLoggedIn,
    getUid: state => state.uid,
    getRole: state => state.role,
    r0: state => state.role === 0,
    r1: state => state.role === 1,
    r2: state => state.role === 2,
    isAdmin: state => state.role !== null && state.role < 2,
    // "half admin" - a regular user assigned to one partner
    canManageAllGames: state => isFullAdmin(state),
    canManageAllUsers: state =>
      isFullAdmin(state) || isPartnerAdmin(state.userDetails),
    canAccessAllUsers: state =>
      isFullAdmin(state) || isPartnerAdmin(state.userDetails),
    isSubAdmin: state => isPartnerAdmin(state.userDetails),
    hasAdminAccess: state =>
      isFullAdmin(state) || isPartnerAdmin(state.userDetails),
    isInstitution: state => !!state.userDetails?.institution,
    hasPartnerFeature: state => key =>
      hasPartnerFeature(state.userDetails, key),
  },

  actions: {
    login(user: User) {
      const switchedUser = String(this.uid || '') !== String(user._id || '')
      this.isLoggedIn = true
      this.userDetails = user
      this.name = user.fullname
      this.role = user.role
      this.uid = user._id
      if (switchedUser) useCategoriesStore().$reset()
    },

    clearData() {
      this.isLoggedIn = false
      this.name = null
      this.role = null
      this.uid = null
      this.userDetails = null
      useCategoriesStore().$reset()
    },

    async checkLogin() {
      let leaving = false
      try {
        const token = extractTokenFromURL()
        if (token) {
          storeTokenInSessionStorage(token)
          removeTokenFromURL()
        }
        const { data } = await axios.post('/auth/checkLogin', {
          lang: this.locale,
          pageOrigin: window.location.origin,
        })
        if (isHostRedirect(data?.redirect)) {
          leaving = true
          window.location.replace(data.redirect)
          return
        }
        if (typeof data?.token === 'string' && data.token) {
          storeTokenInSessionStorage(data.token)
          syncPartnerAuthCookie(data.token)
          delete data.token
        }
        this.login(data)
      } catch (e: any) {
        const isRedirect = e.response?.data?.redirect
        if (isHostRedirect(isRedirect)) {
          leaving = true
          window.location.replace(isRedirect)
          return
        }
        if (e?.response?.status === 401) clearPartnerAuthCookie()
        console.error(e)
        if (isRedirect && isRedirect !== lastRedirect) {
          lastRedirect = isRedirect
          return isRedirect
        }
      } finally {
        if (!leaving) this.checked = true
      }
    },

    async logout() {
      try {
        useAppStore().setLoadingActive(true)
        this.clearData()

        await axios.post('/auth/logout')
        await router.push({ name: 'Login', params: { lang: this.locale } })
      } catch (e: any) {
        console.error(e)
      } finally {
        window.sessionStorage.removeItem('user')
        clearPartnerAuthCookie()
      }
    },
  },
})
